import { apiService, handleResponse } from '@/services/apiService'
import type { BetPayload } from './type'
import { isMockEnabled, mockActiveCourse, mockGenyPronostic, mockProgram, mockStoreBet } from './mock'
import { isEmbedded } from './embed'

/** Clé du token borne (localStorage sur borne physique, sessionStorage en mode app). */
export const KIOSK_TOKEN_STORAGE_KEY = 'kiosk_token'

/**
 * En mode embarqué (iframe dans l'app), le jeton vit en sessionStorage : il reste
 * propre à cette iframe et n'entre pas en collision avec une borne physique ouverte
 * sur le même origine (qui partagerait le localStorage).
 */
function tokenStorage(): Storage {
    return isEmbedded() ? sessionStorage : localStorage
}

/**
 * Récupère le token de la borne depuis le stockage adapté au contexte.
 */
export function getKioskToken(): string {
    return tokenStorage().getItem(KIOSK_TOKEN_STORAGE_KEY) ?? ''
}

/** Enregistre le token de la borne dans le stockage adapté au contexte. */
export function setKioskToken(token: string): void {
    tokenStorage().setItem(KIOSK_TOKEN_STORAGE_KEY, token)
}

/** Supprime le token de la borne du stockage adapté au contexte. */
export function clearKioskToken(): void {
    tokenStorage().removeItem(KIOSK_TOKEN_STORAGE_KEY)
}

/** Indique si un token borne est disponible dans le contexte courant. */
export function hasKioskToken(): boolean {
    return Boolean(getKioskToken())
}

export class KioskRepository {
    /** Header de sécurité anti-smartphone exigé par l'API. */
    private headers() {
        return { headers: { 'X-Kiosk-Token': getKioskToken() } }
    }

    /** Course pariable (départ imminent) ou écran de pause. */
    async getActiveCourse() {
        if (isMockEnabled) {
            return { error: false, data: mockActiveCourse() }
        }
        const response = await apiService.get(`/v1/kiosk/active-course`, this.headers())
        return handleResponse(response)
    }

    /** Programme complet de la réunion en cache. */
    async getProgram() {
        if (isMockEnabled) {
            return { error: false, data: mockProgram() }
        }
        const response = await apiService.get(`/v1/kiosk/program`, this.headers())
        return handleResponse(response)
    }

    /** État de connexion de l'imprimante locale. */
    async getPrinterStatus() {
        try {
            const response = await fetch('http://127.0.0.1:9100/printer-status', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            })

            const data = await response.json().catch(() => null)

            return {
                error: !response.ok,
                data,
                status: response.status,
            }
        } catch (error) {
            return {
                error: true,
                data: null,
                status: 0,
                message: error instanceof Error ? error.message : 'Printer status unavailable',
            }
        }
    }

    /** Enregistre le projet de pari et récupère le QR Code. */
    async storeBet(payload: BetPayload) {
        if (isMockEnabled) {
            return { error: false, data: { success: true, data: mockStoreBet(payload) } }
        }
        const response = await apiService.post(`/v1/kiosk/bets`, payload, this.headers())
        return handleResponse(response)
    }

    /**
     * Récupère les pronostics Geny pour la course active via le back.
     */
    async getGenyPronostic() {
        if (isMockEnabled) {
            return { error: false, data: mockGenyPronostic() }
        }
        const response = await apiService.get(`/v1/kiosk/pronostic`, this.headers())
        return handleResponse(response)
    }

    /** Envoie le ticket de pari (QR Code) par email au parieur connecté. */
    async emailBet(betId: number, email: string) {
        if (isMockEnabled) {
            return { error: false, data: { success: true } }
        }
        const response = await apiService.post(`/v1/kiosk/bets/${betId}/email`, { email }, this.headers())
        return handleResponse(response)
    }
}
